import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../config";
import { CATEGORY_LABELS, RANK_LABELS, SPECIAL_JURY_LABEL } from "../utils/jeedigunta";
import { FaTrophy, FaCheckCircle, FaSpinner, FaExclamationTriangle } from "react-icons/fa";

const JeedigntaWinnerForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contact, setContact] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form fields
  const [recipientName, setRecipientName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [addr1, setAddr1] = useState("");
  const [addr2, setAddr2] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("United States");
  const [payoutChoice, setPayoutChoice] = useState("zelle");
  const [payoutHandle, setPayoutHandle] = useState("");
  const [payoutOther, setPayoutOther] = useState("");
  const [addonCurrency, setAddonCurrency] = useState("");
  const [addonAmount, setAddonAmount] = useState("");
  const [donationMessage, setDonationMessage] = useState("");
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    document.title = "Jeedigunta Kathakala Puraskaralu 2026";
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error: rpcErr } = await supabase.rpc("jeedigunta_get_winner_form", {
        p_token: token,
      });
      if (rpcErr) {
        setError(rpcErr.message || "Could not load form");
        setLoading(false);
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        setError(
          "This link is invalid or has expired. Please contact Bhaskar Rayavaram."
        );
        setLoading(false);
        return;
      }
      setContact(row);
      // Pre-fill from existing data if any.
      setRecipientName(row.recipient_name || "");
      setEmail(row.email || "");
      setPhone(row.phone || "");
      setAddr1(row.address_line1 || "");
      setAddr2(row.address_line2 || "");
      setCity(row.city || "");
      setStateRegion(row.state || "");
      setPostal(row.postal_code || "");
      setCountry(row.country || "United States");
      setPayoutChoice(row.payout_choice || "zelle");
      setPayoutHandle(row.payout_handle || "");
      setPayoutOther(row.payout_other_detail || "");
      setAddonCurrency(row.addon_donation_currency || "");
      setAddonAmount(row.addon_donation_amount?.toString() || "");
      setDonationMessage(row.donation_message || "");
      if (row.token_status === "submitted") {
        setSubmitted(false); // allow edit, but show banner
      }
      setLoading(false);
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consented) {
      setError("Please tick the consent box at the bottom.");
      return;
    }
    if (!recipientName.trim()) {
      setError("Please confirm your name.");
      return;
    }
    if (payoutChoice === "zelle" || payoutChoice === "gpay") {
      if (!payoutHandle.trim()) {
        setError(
          payoutChoice === "zelle"
            ? "Please enter your Zelle email/phone."
            : "Please enter your GPay UPI ID or phone."
        );
        return;
      }
    }
    setSubmitting(true);
    setError("");
    const { error: rpcErr } = await supabase.rpc("jeedigunta_submit_winner_form", {
      p_token: token,
      p_recipient_name: recipientName.trim(),
      p_email: email.trim() || null,
      p_phone: phone.trim() || null,
      p_address_line1: addr1.trim() || null,
      p_address_line2: addr2.trim() || null,
      p_city: city.trim() || null,
      p_state: stateRegion.trim() || null,
      p_postal_code: postal.trim() || null,
      p_country: country.trim() || null,
      p_payout_choice: payoutChoice,
      p_payout_handle: payoutHandle.trim() || null,
      p_payout_other_detail: payoutOther.trim() || null,
      p_addon_donation_currency: addonCurrency || null,
      p_addon_donation_amount: addonAmount ? parseFloat(addonAmount) : null,
      p_donation_message: donationMessage.trim() || null,
      p_consented: true,
    });
    setSubmitting(false);
    if (rpcErr) {
      setError(rpcErr.message || "Could not submit");
      return;
    }
    setSubmitted(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <Wrap>
        <div className="text-center text-kk-cream/70 py-20">
          <FaSpinner className="animate-spin inline mr-2" /> Loading form…
        </div>
      </Wrap>
    );
  }

  if (error && !contact) {
    return (
      <Wrap>
        <div className="max-w-xl mx-auto text-center py-16">
          <FaExclamationTriangle className="text-red-400 text-4xl mx-auto mb-4" />
          <h1 className="text-2xl text-kk-cream font-semibold mb-2">
            We couldn't open this form
          </h1>
          <p className="text-kk-cream/70">{error}</p>
        </div>
      </Wrap>
    );
  }

  if (submitted) {
    return (
      <Wrap>
        <div className="max-w-xl mx-auto text-center py-12">
          <FaCheckCircle className="text-emerald-400 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl text-kk-gold-light font-bold mb-3">
            ధన్యవాదాలు · Thank you!
          </h1>
          <p className="text-kk-cream/85 mb-2">
            Your information has been received. Bhaskar Rayavaram will be in touch
            soon to ship your trophy and arrange your prize.
          </p>
          <p className="text-kk-cream/60 text-sm">
            You can re-open this same link anytime to update your details.
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="mt-6 px-4 py-2 bg-kk-gold/15 border border-kk-gold/40 rounded-md text-kk-gold-light hover:bg-kk-gold/25 text-sm"
          >
            Edit my details
          </button>
        </div>
      </Wrap>
    );
  }

  const catLabel = CATEGORY_LABELS[contact.category] || { te: contact.category, en: contact.category };
  const rankTitle = contact.is_special_jury
    ? SPECIAL_JURY_LABEL
    : RANK_LABELS[contact.rank] || { te: "", en: "" };

  return (
    <Wrap>
      <div className="max-w-2xl mx-auto px-1">
        {/* Header */}
        <header className="text-center mb-8">
          <FaTrophy className="text-kk-gold text-4xl mx-auto mb-3" />
          <h1 className="text-2xl md:text-3xl font-bold text-kk-gold-light mb-1">
            జీడిగుంట కథాకళ పురస్కారాలు
          </h1>
          <p className="text-kk-cream/70 italic font-serif">
            Jeedigunta Kathakala Puraskaralu
          </p>
        </header>

        {/* Recognition card */}
        <div className="mb-8 p-5 bg-gradient-to-br from-kk-maroon-deep/80 to-kk-ink/70 border border-kk-gold/40 rounded-xl text-center">
          <div className="text-kk-gold text-sm uppercase tracking-wide mb-1">
            {contact.season_label || `Season ${contact.season_id}`}
          </div>
          <div className="text-xl md:text-2xl font-bold text-kk-cream mb-1">
            {recipientName || "—"}
          </div>
          <div className="text-kk-gold-light text-base md:text-lg">
            {rankTitle.te} · <span className="italic font-serif">{rankTitle.en}</span>
          </div>
          <div className="text-kk-cream/60 text-sm mt-1">
            {catLabel.te} · <span className="italic font-serif">{catLabel.en}</span>
          </div>
        </div>

        {contact.token_status === "submitted" && (
          <div className="mb-6 p-4 bg-emerald-900/30 border border-emerald-500/30 rounded-lg text-emerald-200 text-sm">
            <FaCheckCircle className="inline mr-2" />
            We already received your details. Edit anything below and re-submit if it
            needs updating.
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-500/40 rounded-lg text-red-200 text-sm">
            <FaExclamationTriangle className="inline mr-2" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <Section title="Your Name">
            <Input
              label="Recipient name (as shown on the trophy)"
              value={recipientName}
              onChange={setRecipientName}
              required
            />
          </Section>

          <Section title="Contact">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Email" type="email" value={email} onChange={setEmail} />
              <Input
                label="Phone (incl. country code)"
                value={phone}
                onChange={setPhone}
                placeholder="+1 312 555 0123"
              />
            </div>
          </Section>

          <Section title="Shipping address (for trophy)">
            <Input label="Address line 1" value={addr1} onChange={setAddr1} />
            <Input label="Address line 2 (optional)" value={addr2} onChange={setAddr2} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="City" value={city} onChange={setCity} />
              <Input
                label="State / Region"
                value={stateRegion}
                onChange={setStateRegion}
              />
              <Input label="Postal code" value={postal} onChange={setPostal} />
            </div>
            <Input label="Country" value={country} onChange={setCountry} />
          </Section>

          <Section title="Prize payment">
            <div className="space-y-2">
              <PayoutOption
                value="zelle"
                current={payoutChoice}
                onSelect={setPayoutChoice}
                title="Zelle"
                hint="USA only · enter the email or phone linked to your Zelle account"
              />
              {payoutChoice === "zelle" && (
                <Input
                  label="Your Zelle email or phone"
                  value={payoutHandle}
                  onChange={setPayoutHandle}
                  placeholder="you@example.com"
                />
              )}

              <PayoutOption
                value="gpay"
                current={payoutChoice}
                onSelect={setPayoutChoice}
                title="GPay (Google Pay UPI)"
                hint="India · enter your UPI ID or phone number"
              />
              {payoutChoice === "gpay" && (
                <Input
                  label="Your GPay UPI ID or phone"
                  value={payoutHandle}
                  onChange={setPayoutHandle}
                  placeholder="name@oksbi"
                />
              )}

              <PayoutOption
                value="donate_sritaru"
                current={payoutChoice}
                onSelect={setPayoutChoice}
                title="Donate my prize to Sri Taru 🌱"
                hint="Your prize amount will go to Sri Taru Sankalpam (tree-planting initiative)"
              />

              <PayoutOption
                value="other"
                current={payoutChoice}
                onSelect={setPayoutChoice}
                title="Other"
                hint="If none of the above work, describe how you'd like to receive payment"
              />
              {payoutChoice === "other" && (
                <>
                  <Input
                    label="Service / handle"
                    value={payoutHandle}
                    onChange={setPayoutHandle}
                    placeholder="e.g., Wise, PayPal, bank transfer"
                  />
                  <Input
                    label="Additional details"
                    value={payoutOther}
                    onChange={setPayoutOther}
                  />
                </>
              )}
            </div>
          </Section>

          <Section
            title="Optional: contribute to Sri Taru"
            subtitle="Add your own contribution to Sri Taru's tree-planting fund (separate from your prize)."
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-kk-cream/70 mb-1">Currency</label>
                <select
                  value={addonCurrency}
                  onChange={(e) => setAddonCurrency(e.target.value)}
                  className="w-full bg-kk-ink/60 border border-kk-gold/30 rounded-md px-3 py-2 text-kk-cream"
                >
                  <option value="">— none —</option>
                  <option value="USD">USD ($)</option>
                  <option value="INR">INR (₹)</option>
                </select>
              </div>
              <Input
                label="Amount"
                type="number"
                value={addonAmount}
                onChange={setAddonAmount}
                placeholder="0.00"
              />
              <div className="md:col-span-1" />
            </div>
            <Input
              label="Optional message (will be shared on the Sri Taru thank-you wall)"
              value={donationMessage}
              onChange={setDonationMessage}
            />
          </Section>

          <div className="p-4 bg-kk-ink/50 border border-kk-gold/30 rounded-lg">
            <label className="flex gap-3 items-start cursor-pointer">
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                className="mt-1 w-5 h-5 accent-kk-gold"
                required
              />
              <span className="text-sm text-kk-cream/85">
                I confirm the information above is correct and authorize the
                Kathakala team to use it to ship my trophy and transfer my prize.
              </span>
            </label>
          </div>

          <div className="flex flex-col md:flex-row gap-3 items-center pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="w-full md:flex-1 bg-gradient-to-r from-kk-gold to-kk-amber text-kk-ink font-bold px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(212,168,67,0.4)] transition-all disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <FaSpinner className="animate-spin inline mr-2" /> Submitting…
                </>
              ) : (
                "Submit my details"
              )}
            </button>
            <Link
              to="/jeedigunta"
              className="text-kk-cream/60 hover:text-kk-gold text-sm"
            >
              View Puraskaralu site →
            </Link>
          </div>
        </form>

        <p className="mt-10 text-center text-xs text-kk-cream/40">
          This link is unique to you. Please do not share it.
        </p>
      </div>
    </Wrap>
  );
};

const Wrap = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-b from-kk-ink via-kk-maroon-deep to-kk-ink text-kk-cream pt-4 pb-10 md:pt-6 md:pb-12">
    <div className="max-w-3xl mx-auto px-4">{children}</div>
  </div>
);

const Section = ({ title, subtitle, children }) => (
  <fieldset className="p-5 bg-kk-ink/40 border border-kk-gold/20 rounded-xl">
    <legend className="px-2 text-kk-gold-light font-semibold text-sm">{title}</legend>
    {subtitle && <p className="text-xs text-kk-cream/60 -mt-1 mb-3">{subtitle}</p>}
    <div className="space-y-3">{children}</div>
  </fieldset>
);

const Input = ({ label, value, onChange, type = "text", required, placeholder }) => (
  <div>
    <label className="block text-xs text-kk-cream/70 mb-1">
      {label}
      {required && <span className="text-red-400"> *</span>}
    </label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="w-full bg-kk-ink/60 border border-kk-gold/30 rounded-md px-3 py-2 text-kk-cream placeholder-kk-cream/30 focus:outline-none focus:border-kk-gold"
    />
  </div>
);

const PayoutOption = ({ value, current, onSelect, title, hint }) => {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        active
          ? "bg-kk-gold/15 border-kk-gold/60"
          : "bg-kk-ink/40 border-kk-gold/20 hover:border-kk-gold/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
            active ? "border-kk-gold bg-kk-gold" : "border-kk-gold/40"
          }`}
        />
        <div className="flex-1">
          <div className="text-kk-cream font-semibold text-sm">{title}</div>
          {hint && <div className="text-kk-cream/55 text-xs mt-0.5">{hint}</div>}
        </div>
      </div>
    </button>
  );
};

export default JeedigntaWinnerForm;
